import React, { useState, useEffect } from "react";
import "./TrngCourse.css";
import * as XLSX from 'xlsx';
import ActionBar from '../Components/BottomActionBar/ActionBar';
import UpdateComponent from '../Components/UpdateComponent/UpdateComponent';
import { trngCourseAPI, batchStatusAPI } from './api';
import { getFuncAreas, getAllTrngCourses, invalidateTrngCourses, invalidateAllTrngCourses } from './cache';
import DataTable from './DataTable';

function TrngCourse() {
  const [funcAreaCode, setFuncAreaCode] = useState('DEFA');
  const [funcSubAreaCode, setFuncSubAreaCode] = useState('');
  const [funcSubAreaName, setFuncSubAreaName] = useState('');
  const [trngCourseName, setTrngCourseName] = useState('');
  const [trngCourseRemark, setTrngCourseRemark] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [funcAreaOptions, setFuncAreaOptions] = useState([]);
  const [funcAreaSelect, setFuncAreaSelect] = useState('none');
  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    loadFuncAreas();
  }, []);

  const loadFuncAreas = async () => {
    try {
      const data = await getFuncAreas();
      setFuncAreaOptions(data);
    } catch (error) {
      // skip
    }
  };

  const handleFuncAreaChange = (e) => {
    const value = e.target.value;
    setFuncAreaSelect(value);
    if (value === 'none') {
      setFuncSubAreaCode('');
      setFuncSubAreaName('');
      return;
    }
    const [subCode, name] = value.split('|');
    setFuncSubAreaCode(subCode);
    setFuncSubAreaName(name);
  };

  const buildParams = () => {
    const params = {};
    if (funcAreaCode.trim()) params.func_area_code = funcAreaCode.trim() === 'DEFA' ? 'DEFA' : funcAreaCode.trim() === 'FREE' ? 'FREE' : funcAreaCode.trim();
    if (funcSubAreaCode.trim()) params.func_sub_area_code = funcSubAreaCode.trim();
    if (trngCourseName.trim()) params.trng_course_name = trngCourseName.trim();
    if (activeStatus === 'Active') params.active_status = '1';
    else if (activeStatus === 'Inactive') params.active_status = '0';
    return params;
  };

  const handleInsert = async () => {
    setUpdateMsg(null);
    setDeleteMsg(null);
    
    if (!funcAreaCode.trim() || !funcSubAreaCode.trim() || !trngCourseName.trim() || !activeStatus) {
      alert('Functional Area Code, Training Course Name and Active Status are required fields.');
      return;
    }

    // Duplicate check using cached training courses
    try {
      const allCourses = await getAllTrngCourses();
      const duplicate = allCourses.find(
        (c) =>
          String(c.func_area_code).toUpperCase() === funcAreaCode.trim().toUpperCase() &&
          String(c.func_sub_area_code).toUpperCase() === funcSubAreaCode.trim().toUpperCase() &&
          String(c.trng_course_name).toUpperCase() === trngCourseName.trim().toUpperCase()
      );
      if (duplicate) {
        setInsertMsg({ text: `Training course "${trngCourseName.trim()}" already exists for this Functional Area.`, success: false });
        return;
      }
    } catch (e) {
      // skip
    }
    
    try {
      const res = await trngCourseAPI.create({
        func_area_code: funcAreaCode === 'DEFA' ? 'DEFA' : funcAreaCode === 'FREE' ? 'FREE' : funcAreaCode,
        func_sub_area_code: funcSubAreaCode,
        trng_course_name: trngCourseName,
        ...(trngCourseRemark.trim() !== '' && { trng_course_remark: trngCourseRemark }),
        active_status: activeStatus === 'Inactive' ? '0' : '1',
      });
      if (res.ok) { invalidateTrngCourses(); invalidateAllTrngCourses(); setInsertMsg({ text: 'Record inserted successfully.', success: true }); }
      else setInsertMsg({ text: 'Insert failed.', success: false });
    } catch {
      setInsertMsg({ text: 'Insert failed.', success: false });
    }
  };

  const handleUpdate = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(true);
    setEditRows({});
    const data = await trngCourseAPI.getAll(buildParams());
    setTableData(data);
    setShowTable(true);
  };

  const toggleEditRow = (idx, row) => {
    setEditRows(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { 
        ...prev, 
        [idx]: { 
          trng_course_name: row.trng_course_name, 
          trng_course_remark: row.trng_course_remark,
          active_status: (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive',
          func_area_code: row.func_area_code === 'Defa' ? 'DEFA' : row.func_area_code === 'Free' ? 'FREE' : row.func_area_code,
        } 
      };
    });
    
    setSelectedForUpdate(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete(prev => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const selectedIndexes = Object.keys(selectedForUpdate).filter(idx => selectedForUpdate[idx]);
    
    if (selectedIndexes.length === 0) {
      setUpdateMsg({ text: 'No records selected for update.', success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      const editedData = editRows[idx];
      
      if (!editedData) continue;

      try {
        const res = await trngCourseAPI.update(row.func_area_code, row.func_sub_area_code, row.trng_course_code, {
          func_area_code: editedData.func_area_code ?? row.func_area_code,
          func_sub_area_code: row.func_sub_area_code,
          trng_course_name: editedData.trng_course_name,
          trng_course_remark: editedData.trng_course_remark,
          active_status: editedData.active_status === 'Active' ? '1' : '0',
        });
        
        if (res.ok) {
          successCount++;
        } else {
          await res.json();
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      invalidateTrngCourses(); invalidateAllTrngCourses();
      setUpdateMsg({ text: `${successCount} record(s) updated successfully.`, success: true });
      const data = await trngCourseAPI.getAll(buildParams());
      setTableData(data);
    }
    if (failCount > 0) {
      setUpdateMsg({ text: `${failCount} record(s) failed to update.`, success: false });
    }

    setSelectedForUpdate({});
    setEditRows({});
  };

  const handleDeleteSelected = async () => {
    const selectedIndexes = Object.keys(selectedForDelete).filter(idx => selectedForDelete[idx]);
    
    if (selectedIndexes.length === 0) {
      setDeleteMsg({ text: 'No records selected for delete.', success: false });
      return;
    }

    let successCount = 0;
    const failMsgs = [];

    for (const idx of selectedIndexes) {
      const row = tableData[idx];

      try {
        const inUseRes = await batchStatusAPI.checkInUse({ trng_course_code: row.trng_course_code });
        if (inUseRes.in_use) {
          alert('The Training Course is currently in use.');
          continue;
        }
        const res = await trngCourseAPI.delete(row.func_area_code, row.func_sub_area_code, row.trng_course_code);
        
        if (res.ok) {
          successCount++;
        } else {
          const errBody = await res.json().catch(() => ({}));
          failMsgs.push(errBody.message || `Failed to delete course ${row.trng_course_code}.`);
        }
      } catch (error) {
        failMsgs.push(`Failed to delete course ${row.trng_course_code}.`);
      }
    }

    if (successCount > 0) {
      invalidateTrngCourses(); invalidateAllTrngCourses();
      setDeleteMsg({ text: `${successCount} record(s) deleted successfully.`, success: true });
      const data = await trngCourseAPI.getAll(buildParams());
      setTableData(data);
    }
    if (failMsgs.length > 0) {
      setDeleteMsg({ text: failMsgs.join(' '), success: false });
    }

    setSelectedForDelete({});
  };

  const handleExport = () => {
    const headers = ['Functional Area', 'Training Course Code', 'Training Course Name', 'Training Course Description', 'Course Type', 'Active Status'];
    const data = tableData.map((row) => {
      const funcArea = funcAreaOptions.find(area => area.func_sub_area_code === row.func_sub_area_code);
      const funcAreaName = funcArea?.func_sub_area_name || row.func_sub_area_code || '';
      return [
        `${funcAreaName} [${row.func_sub_area_code}]`,
        row.trng_course_code,
        row.trng_course_name,
        row.trng_course_remark,
        row.func_area_code === 'Defa' ? 'DEFA' : row.func_area_code === 'Free' ? 'FREE' : row.func_area_code,
        row.active_status === '1' || row.active_status === 'Active' ? 'Active' : 'Inactive',
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Training Course');
    XLSX.writeFile(wb, 'TrngCourse.xlsx');
  };

  const handleView = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(false);
    setEditRows({});
    const data = await trngCourseAPI.getAll(buildParams());
    setTableData(data);
    setShowTable(true);
  };

  const clearMessages = () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const handleReset = () => {
    setFuncAreaCode('DEFA');
    setFuncSubAreaCode('');
    setFuncSubAreaName('');
    setTrngCourseName('');
    setTrngCourseRemark('');
    setActiveStatus('');
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setTableData([]);
    setShowTable(false);
    setSortBy('');
    setFuncAreaSelect('none');
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sortedData = [...data];
    switch(sortOption) {
      case 'Training Course Code':
        return sortedData.sort((a, b) => Number(a.trng_course_code || 0) - Number(b.trng_course_code || 0));
      case 'Training Course Name':
        return sortedData.sort((a, b) => String(a.trng_course_name || '').localeCompare(String(b.trng_course_name || '')));
      case 'Functional Area':
        return sortedData.sort((a, b) => String(a.func_sub_area_code || '').localeCompare(String(b.func_sub_area_code || '')));
      case 'Active Status':
        return sortedData.sort((a, b) => (b.active_status === '1' ? 1 : 0) - (a.active_status === '1' ? 1 : 0));
      default:
        return sortedData;
    }
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) {
      const sorted = sortTableData(tableData, sortOption);
      setTableData(sorted);
    }
  };

  return (
    <div className='Body' onClick={clearMessages}>
      <form>
        <table className='formTable'>
          <tbody>
            <tr>
              <td className='tdLabel'>Course Type:</td>
              <td className='tdField'>
                <label>
                  <input 
                    type="radio" 
                    name="courseType"
                    value="DEFA"
                    checked={funcAreaCode === 'DEFA'} 
                    onChange={(e) => setFuncAreaCode(e.target.value)}
                  />
                  {' '}Godrej
                </label>
              </td>
              <td className='tdField'>
                <label>
                  <input 
                    type="radio" 
                    name="courseType"
                    value="FREE"
                    checked={funcAreaCode === 'FREE'} 
                    onChange={(e) => setFuncAreaCode(e.target.value)}
                  />
                  {' '}Freelancer
                </label>
              </td>
              <td></td>
            </tr>
            
            <tr>
              <td className='tdLabel'>
                <select className='funcAreaSelect' value={funcAreaSelect} onChange={handleFuncAreaChange} onFocus={clearMessages}>
                  <option value="none" disabled>Functional Area Code</option>
                  {funcAreaOptions.map((area, idx) => (
                    <option key={idx} value={`${area.func_sub_area_code}|${area.func_sub_area_name}`}>
                      {area.func_sub_area_name}
                    </option>
                  ))}
                </select>
                <span className='important'>*</span>
              </td>
              <td className='tdField'>
                <input className='funcCodeInput' value={funcSubAreaCode} readOnly={true} />
              </td>
              <td className='tdLabel' style={{ textAlign: 'right' }}>Name</td>
              <td className='tdField'>
                <input className='funcNameInput' value={funcSubAreaName} readOnly={true} />
              </td>
            </tr>
            
            <tr>
              <td className='tdLabel'>
                Training Course Name<span className='important'>*</span>
              </td>
              <td className='tdField' colSpan={3}>
                <input 
                  className='courseNameInput' 
                  type='text' 
                  value={trngCourseName}
                  onChange={(e) => setTrngCourseName(e.target.value)}
                  onBlur={(e) => setTrngCourseName(e.target.value.trim())}
                  onFocus={clearMessages}
                />
              </td>
            </tr>
            
            <tr>
              <td className='tdLabel' style={{ verticalAlign: 'top' }}>
                Training Course Description
              </td>
              <td className='tdField' colSpan={3}>
                <textarea 
                  className='courseDescTextarea' 
                  rows={3}
                  value={trngCourseRemark}
                  onChange={(e) => setTrngCourseRemark(e.target.value)}
                  onFocus={clearMessages}
                ></textarea>
              </td>
            </tr>
            
            <tr>
              <td className='tdLabel'>Active Status <span className='important'>*</span></td>
              <td className='tdField' colSpan={3}>
                <label>
                  <input type="radio" name="activeStatus" value="Active" checked={activeStatus === 'Active'} onChange={() => setActiveStatus('Active')} />{' '}Active
                </label>
                &nbsp;&nbsp;&nbsp;
                <label>
                  <input type="radio" name="activeStatus" value="Inactive" checked={activeStatus === 'Inactive'} onChange={() => setActiveStatus('Inactive')} />{' '}Inactive
                </label>
              </td>
            </tr>
            
            <tr>
              <td colSpan={4}>
                <p className='remarks'>
                  <span className='important'>*</span> indicates a required field for
                  'INSERT' &amp; 'UPDATE'.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <ActionBar 
        sortOptions={['Training Course Code', 'Training Course Name', 'Functional Area', 'Active Status']}
        onView={handleView}
        onReset={handleReset}
        onInsert={handleInsert}
        onUpdate={handleUpdate}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />
      
      {insertMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: insertMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: updateMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: 'center', fontSize: '11px', fontFamily: '"GEG-Body"', color: deleteMsg.success ? 'green' : 'red', margin: '4px 0' }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={[
            {
              header: 'Functional Area',
              accessor: (row) => {
                const funcArea = funcAreaOptions.find(area => area.func_sub_area_code === row.func_sub_area_code);
                const funcAreaName = funcArea?.func_sub_area_name || row.func_sub_area_code || '';
                return `${funcAreaName} [${row.func_sub_area_code}]`;
              },
            },
            { header: 'Training Course Code', accessor: (row) => row.trng_course_code },
            { header: 'Training Course Name', key: 'trng_course_name', accessor: (row) => row.trng_course_name, editable: true },
            { header: 'Training Course Description', key: 'trng_course_remark', accessor: (row) => row.trng_course_remark, editable: true },
            {
              header: 'Course Type',
              key: 'func_area_code',
              accessor: (row) => row.func_area_code === 'DEFA' || row.func_area_code === 'Defa' ? 'Godrej' : row.func_area_code === 'FREE' || row.func_area_code === 'Free' ? 'Freelancer' : row.func_area_code,
              editable: true,
              renderEdit: (idx, editRows, onChangeEditCell) => (
                <div>
                  <label>
                    <input type="radio" checked={editRows[idx]?.func_area_code === 'DEFA' || editRows[idx]?.func_area_code === 'Defa'} onChange={() => onChangeEditCell(idx, 'func_area_code', 'DEFA')} />{' '}Godrej
                  </label>
                  &nbsp;
                  <label>
                    <input type="radio" checked={editRows[idx]?.func_area_code === 'FREE' || editRows[idx]?.func_area_code === 'Free'} onChange={() => onChangeEditCell(idx, 'func_area_code', 'FREE')} />{' '}Freelancer
                  </label>
                </div>
              ),
            },
            {
              header: 'Active Status',
              key: 'active_status',
              accessor: (row) => (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive',
              editable: true,
              renderEdit: (idx, editRows, onChangeEditCell) => (
                <input
                  type="checkbox"
                  checked={editRows[idx]?.active_status === 'Active'}
                  onChange={(e) => onChangeEditCell(idx, 'active_status', e.target.checked ? 'Active' : 'Inactive')}
                />
              ),
            },
          ]}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) =>
            setEditRows(prev => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))
          }
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          emptyMessage="No data found"
          rowKey={(row, idx) => `${row.func_area_code}-${row.func_sub_area_code}-${row.trng_course_code}-${idx}`}
        />
      )}
      {showTable && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button
            style={{ backgroundColor: '#810055', color: '#fff', border: 'none', padding: '5px 22px', borderRadius: '20px', cursor: 'pointer', fontFamily: '"GEG-Body"', fontSize: '12.5px' }}
            onClick={handleExport}
          >
            Export To Excel
          </button>
        </div>
      )}
      {showTable && showEditCols && (
        <UpdateComponent
          onUpdateSelected={handleUpdateSelected}
          onDeleteSelected={handleDeleteSelected}
        />
      )}
    </div>
  );
}

export default TrngCourse;
